import React, { useRef, useState, useEffect } from 'react';
import Formulario from './Formulario';

// Detectar móvil
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const VideoPlayer = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [currentSrc, setCurrentSrc] = useState('/videos/intro.webm');
  const [isPaused, setIsPaused] = useState(true);
  const [volume, setVolume] = useState(1);
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);

  // Intento de autoplay al cargar (silenciado)
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      video.playsInline = true;
      video
        .play()
        .then(() => {
          setIsPaused(false);
          setShowPlayOverlay(false);
        })
        .catch(() => {
          setShowPlayOverlay(true);
        });
    }
  }, []);

  // Pausar intro y mostrar formulario a los 34.19s
  useEffect(() => {
    const video = videoRef.current;
    const onTimeUpdate = () => {
      if (!showForm && !formSubmitted && video.currentTime >= 34.20) {
        video.pause();
        setIsPaused(true);
        setShowForm(true);
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [showForm, formSubmitted]);

  // Actualizar estado de reproducción
  useEffect(() => {
    const video = videoRef.current;
    const onPlay = () => setIsPaused(false);
    const onPause = () => setIsPaused(true);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  // Cuando cambie currentSrc, recarga y reproduce automáticamente
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.load();                  // recarga las <source>
      video.play().catch(() => {});  // intenta reproducir
    }
  }, [currentSrc]);

  // Enviar formulario y reanudar vídeo
  const handleFormSubmit = async (values) => {
    const apiUrl =
      process.env.NODE_ENV === 'production' ? '/sendEmail.php' : '/api/send-email';
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error('Error en servidor');
      setFormSubmitted(true);
      setShowForm(false);
      videoRef.current.play();
    } catch {
      alert('Error al enviar tu información. Intenta de nuevo.');
    }
  };

  // Al finalizar un vídeo
  const handleEnded = () => {
    if (currentSrc.includes('intro.webm')) {
      setCurrentSrc('/videos/full.webm');
    } else {
      window.location.href = 'https://calendly.com/biancafinanzas/zoom-meeting-1';
    }
  };

  // Alternar play/pause y ocultar overlay de play
  const togglePlay = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.muted = false;
      video.play();
      setShowPlayOverlay(false);
    } else {
      video.pause();
    }
  };

  // Volumen y desmutear
  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    const video = videoRef.current;
    video.volume = v;
    if (v > 0) video.muted = false;
  };

  // Fullscreen y bloqueo en landscape en móvil
  const toggleFullScreen = async () => {
    const el = containerRef.current;
    const orientation = window.screen.orientation;
    if (!document.fullscreenElement) {
      await el.requestFullscreen();
      if (isMobile() && orientation.lock) {
        try { await orientation.lock('landscape'); } catch {}
      }
    } else {
      await document.exitFullscreen();
      if (isMobile() && orientation.unlock) {
        try { orientation.unlock(); } catch {}
      }
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <video
        ref={videoRef}
        muted
        playsInline
        onEnded={handleEnded}
        onLoadedData={() => {}}
        style={{ width: '100%', display: 'block' }}
      >
        {/* WebM primero */}
        <source src={currentSrc} type="video/webm" />
        {/* Fallback MP4 */}
        <source src={currentSrc.replace('.webm', '.mp4')} type="video/mp4" />
      </video>

      {/* Overlay Play */}
      {showPlayOverlay && !showForm && (
        <div
          className="play-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            cursor: 'pointer',
          }}
          onClick={togglePlay}
        >
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#000">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}

      {/* Controles personalizados */}
      <div
        className="custom-controls"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          padding: '0.5rem',
          boxSizing: 'border-box',
        }}
      >
        <button
          onClick={togglePlay}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem' }}
        >
          {isPaused ? 'Play' : 'Pause'}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
          style={{ cursor: 'pointer' }}
        />
        <button
          onClick={toggleFullScreen}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem' }}
        >
          {document.fullscreenElement ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
      </div>

      {/* Overlay del formulario */}
      {showForm && (
        <div
          className="overlay-form"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 20,
          }}
        >
          <Formulario onSubmit={handleFormSubmit} />
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
